import { useEffect, useState } from 'react';
import { uid } from '../../lib/helpers/uid';
import { ProductImage } from '../product/ProductImage';
import { DimensionAndWeightDisplay } from '../smartsend/DimensionDisplay';
import { Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GGrid, P2GRadio, P2GText } from '@parcel2go/core-ui';
import P2GOptionContainer from '~/components/option-container/option-container';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  packages: () => Promise<PackageApiModel[]>;
  onSelect: (p: Dimensions & { Name?: string }) => void;
  selected: Dimensions;
  children: React.ReactNode;
}
export const AutoPackageList: React.FC<props> = (props) => {
  const [packages, setPackages] = useState<PackageApiModel[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    if (props.packages) {
      props.packages().then((r) => {
        setPackages(
          r.map((x) => {
            return { ...x, Id: uid() };
          })
        );
      });
    }
  }, []);

  const generateDimensionKey = (x: Dimensions) => {
    const sanitisedDims = [x.Length || 0, x.Height || 0, x.Width || 0].sort();
    return `${sanitisedDims[0]}|${sanitisedDims[1]}|${sanitisedDims[2]}`;
  };

  const isSelected = (x: Dimensions, y: Dimensions) =>
    generateDimensionKey(x) == generateDimensionKey(y) && (!x.Weight || x.Weight == y.Weight);

  const hasSelection = packages.some((x) => isSelected(x, props.selected));
  const hasImages = SettingsCache.settings.Features.includes('PackageImages') && !!packages.find((x) => x.Thumb);

  return (
    <P2GGrid container spacing={2} alignItems="center">
      {packages.length > 0 && (
        <P2GGrid item xs={12}>
          <P2GGrid container spacing={1}>
            {packages
              .sort((a, b) => a.Weight - b.Weight)
              .map((x) => (
                <P2GGrid item key={x.Id} xs={12} sm={6}>
                  <P2GOptionContainer variant="package-option">
                    <P2GRadio
                      name="Packages"
                      value={generateDimensionKey(props.selected)}
                      checked={isSelected(x, props.selected)}
                      id={x.Id}
                      onChange={() => props.onSelect(x)}
                      label={
                        <P2GGrid container gap={1} wrap="nowrap">
                          {hasImages && (
                            <P2GGrid item>
                              <ProductImage product={{ ImageUrl: x.Thumb }} style={{ height: '20px' }} />
                            </P2GGrid>
                          )}
                          <P2GGrid item>
                            <div>
                              <P2GText variant="body2">
                                <strong>{x.Name}</strong>
                              </P2GText>
                            </div>
                            <DimensionAndWeightDisplay dimensions={x} variant="body2" />
                          </P2GGrid>
                        </P2GGrid>
                      }
                    />
                  </P2GOptionContainer>
                </P2GGrid>
              ))}
            <P2GGrid item xs={12} sm={6}>
              <P2GOptionContainer variant="package-option">
                <P2GRadio
                  name="Packages"
                  value={''}
                  checked={!hasSelection}
                  id={'Fake'}
                  onChange={() =>
                    props.onSelect({ Weight: props.selected.Weight, Height: 10, Length: 10, Width: 10, Name: '' })
                  }
                  label={
                    <P2GGrid container gap={1} wrap="nowrap">
                      {hasImages && (
                        <P2GGrid item>
                          <ProductImage
                            product={{
                              ImageUrl:
                                'https://content.parcelsolutions.net/42c5534b-0f47-4342-80f0-8f21203f0669/smart-send/defaults/custom-over-active.svg'
                            }}
                            style={{ height: '20px' }}
                          />
                        </P2GGrid>
                      )}
                      <P2GGrid item>
                        <div>
                          <P2GText variant="body2">
                            <Trans t={t} i18nKey="CUSTOM_PACKAGING" defaults="<strong>Custom Dimensions</strong>" />
                          </P2GText>
                        </div>
                      </P2GGrid>
                    </P2GGrid>
                  }
                />
              </P2GOptionContainer>
            </P2GGrid>
          </P2GGrid>
        </P2GGrid>
      )}
      {!hasSelection && props.children}
    </P2GGrid>
  );
};
